import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { MA_CHUNG_TU, QUERY_KEYS } from '@/constants';
import { useDonHangExport } from '../hooks';
import { useCRUD } from '@/hooks';

interface DonHangSearchContextValue {
  exportToExcel: () => Promise<void>;
  canExport: boolean;
  isExporting: boolean;
  transactionOptions: { value: string; label: string }[];
}

const DonHangSearchContext = createContext<DonHangSearchContextValue | null>(null);

export function useDonHangSearchContext() {
  const context = useContext(DonHangSearchContext);

  if (!context) {
    throw new Error('useDonHangSearchContext must be used within DonHangSearchProvider');
  }

  return context;
}

export function DonHangSearchProvider({ children }: { children: ReactNode }) {
  const { exportToExcel, canExport, isExporting } = useDonHangExport();
  const { data } = useCRUD({
    endpoint: `${QUERY_KEYS.MA_GIAO_DICH}?ma_ct=${MA_CHUNG_TU.BAN_HANG.DON_HANG}`
  });

  const transactionOptions = useMemo(
    () =>
      data.map(item => ({
        value: item.uuid,
        label: item.ten_gd
      })),
    [data]
  );

  const value = useMemo(
    () => ({
      exportToExcel,
      canExport,
      isExporting,
      transactionOptions
    }),
    [canExport, exportToExcel, isExporting, transactionOptions]
  );

  return <DonHangSearchContext.Provider value={value}>{children}</DonHangSearchContext.Provider>;
}
